// مدارک حرفه‌ای ارائه‌دهنده در تب ارائه‌دهنده SCR-STAFF-921 (US-IAM-022، AP-IAM-05): فهرست با وضعیت انقضا (معتبر، رو به انقضا ≤ ۳۰ روز،
// منقضی)، مدرک الزامی ثبت‌نشده (BR-IAM-021)، ثبت و ویرایش و تمدید (AC-IAM-022.1)، پیوست PDF/PNG/JPEG، دانلود، استعلام (Mock — US-IAM-023)
// و حذف. انقضا ارائه‌دهنده را غیرفعال نمی‌کند (BR-IAM-022).
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type CredentialType,
  type CredentialValidityState,
  type ProviderCredentialView,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  ConfirmDialog,
  ErrorState,
  faDigits,
  JalaliDatePickerRHF,
  LoadingState,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  toJalali,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { saveBlob } from '../settings/import/common';

/** انواع مدرک (AC-IAM-022.1). */
export const CREDENTIAL_TYPES: readonly CredentialType[] = [
  'MedicalCouncilCode',
  'NursingCouncilCode',
  'PsychologyCouncilCode',
  'PracticeLicense',
  'SpecialtyCertificate',
  'CourseCertificate',
  'Other',
];

export const credentialKeys = {
  list: (tenantId: string | undefined, providerId: string) =>
    ['identity', tenantId, 'admin', 'provider', providerId, 'credentials'] as const,
  alerts: (tenantId: string | undefined) => ['identity', tenantId, 'credential-alerts'] as const,
};

/** رنگ وضعیت انقضا. */
export function validityTone(state: CredentialValidityState): StatusTone {
  switch (state) {
    case 'Valid':
      return 'active';
    case 'Expiring':
      return 'pending';
    default:
      return 'error';
  }
}

const credentialSchema = z.object({
  type: z.enum(CREDENTIAL_TYPES as [CredentialType, ...CredentialType[]], { error: 'common:validation.required' }),
  qualifier: z.string().max(64, { error: 'common:validation.too_long' }),
  number: z.string().trim().min(1, { error: 'common:validation.required' }).max(64, { error: 'common:validation.too_long' }),
  issuer: z.string().trim().min(1, { error: 'common:validation.required' }).max(200, { error: 'common:validation.too_long' }),
  issuedOn: z.string().nullable(),
  expiresOn: z.string().nullable(),
});

type CredentialForm = z.infer<typeof credentialSchema>;

function CredentialDialog({
  providerId,
  credential,
  onClose,
}: {
  providerId: string;
  credential: ProviderCredentialView | null;
  onClose: () => void;
}) {
  const { t } = useTranslation('iam');
  const titleId = useId();
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [error, setError] = useState<unknown>();
  const { control, handleSubmit, setError: setFieldError } = useForm<CredentialForm>({
    resolver: zodResolver(credentialSchema),
    defaultValues: {
      type: credential?.type ?? 'MedicalCouncilCode',
      qualifier: credential?.qualifier ?? '',
      number: credential?.number ?? '',
      issuer: credential?.issuer ?? '',
      issuedOn: credential?.issuedOn ?? null,
      expiresOn: credential?.expiresOn ?? null,
    },
  });

  const save = useMutation({
    mutationFn: (form: CredentialForm) => {
      const body = {
        type: form.type,
        qualifier: form.qualifier.trim() || null,
        number: form.number.trim(),
        issuer: form.issuer.trim(),
        issuedOn: form.issuedOn,
        expiresOn: form.expiresOn,
      };
      return credential
        ? unwrap(
            tenantApi.PUT('/api/v1/identity/providers/{providerId}/credentials/{credentialId}', {
              params: { path: { providerId, credentialId: credential.id } },
              body,
            }),
          )
        : unwrap(
            tenantApi.POST('/api/v1/identity/providers/{providerId}/credentials', {
              params: { path: { providerId } },
              body,
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
          );
    },
    onMutate: () => setError(undefined),
    onSettled: (_data, failure) => {
      if (!credential) {
        idempotency.settle(failure ?? undefined);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: credentialKeys.list(session?.tenantId, providerId) });
      void queryClient.invalidateQueries({ queryKey: credentialKeys.alerts(session?.tenantId) });
      onClose();
    },
    onError: (failure) => {
      const applied = applyServerErrors(failure, setFieldError, {
        fields: ['type', 'qualifier', 'number', 'issuer', 'issuedOn', 'expiresOn'],
        codeFields: { 'PHX-IAM-0405': 'number' },
      });
      setError(applied > 0 ? undefined : failure);
    },
  });

  return (
    <Dialog open onClose={save.isPending ? undefined : onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>{t(credential ? 'iam:credential.edit' : 'iam:credential.add')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id={titleId + '-form'}
          spacing={2}
          sx={{ pt: 1 }}
          onSubmit={(event) => void handleSubmit((form) => save.mutate(form))(event)}
          data-testid="credential-form"
        >
          <SelectRHF
            name="type"
            control={control}
            labelKey="iam:credential.type"
            required
            options={CREDENTIAL_TYPES.map((type) => ({ value: type, labelKey: `iam:credential.types.${type}` }))}
          />
          <TextFieldRHF name="qualifier" control={control} labelKey="iam:credential.qualifier" helperKey="iam:credential.qualifier_hint" />
          <TextFieldRHF name="number" control={control} labelKey="iam:credential.number" required />
          <TextFieldRHF name="issuer" control={control} labelKey="iam:credential.issuer" required />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <JalaliDatePickerRHF name="issuedOn" control={control} labelKey="iam:credential.issued_on" />
            <JalaliDatePickerRHF name="expiresOn" control={control} labelKey="iam:credential.expires_on" />
          </Stack>
          {error ? <ErrorState error={error} compact /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button type="submit" form={titleId + '-form'} variant="contained" disabled={save.isPending} data-testid="credential-save">
          {t('common:actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CredentialRow({ providerId, credential, canWrite, onEdit }: { providerId: string; credential: ProviderCredentialView; canWrite: boolean; onEdit: () => void }) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [actionError, setActionError] = useState<unknown>();
  const path = { providerId, credentialId: credential.id };
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: credentialKeys.list(session?.tenantId, providerId) });
    void queryClient.invalidateQueries({ queryKey: credentialKeys.alerts(session?.tenantId) });
  };

  const attach = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return unwrap(
        tenantApi.POST('/api/v1/identity/providers/{providerId}/credentials/{credentialId}/attachment', {
          params: { path },
          body: form as never,
          bodySerializer: (body: unknown) => body as FormData,
        }),
      );
    },
    onMutate: () => setActionError(undefined),
    onSuccess: refresh,
    onError: setActionError,
  });
  const verify = useMutation({
    mutationFn: () =>
      unwrap(tenantApi.POST('/api/v1/identity/providers/{providerId}/credentials/{credentialId}:verify', { params: { path } })),
    onMutate: () => setActionError(undefined),
    onSuccess: refresh,
    onError: setActionError,
  });
  const remove = useMutation({
    mutationFn: () =>
      unwrap(tenantApi.DELETE('/api/v1/identity/providers/{providerId}/credentials/{credentialId}', { params: { path } })),
    onSuccess: () => {
      setConfirm(false);
      refresh();
    },
  });

  const download = async () => {
    setActionError(undefined);
    const result = await tenantApi.GET('/api/v1/identity/providers/{providerId}/credentials/{credentialId}/attachment', {
      params: { path },
      parseAs: 'blob',
    });
    if (result.response.ok && result.data) {
      saveBlob(result.data as Blob, credential.attachment?.fileName ?? 'credential');
    } else {
      setActionError(result.error ?? new Error('download'));
    }
  };

  const days = credential.daysToExpiry;
  return (
    <>
      <TableRow data-testid="credential-row" data-type={credential.type}>
        <TableCell>
          {t(`iam:credential.types.${credential.type}`)}
          {credential.qualifier ? <Typography variant="caption" display="block">{credential.qualifier}</Typography> : null}
          {credential.required ? <Typography variant="caption" color="primary" display="block">{t('iam:credential.required')}</Typography> : null}
        </TableCell>
        <TableCell>
          <bdi>{faDigits(credential.number)}</bdi>
          <Typography variant="caption" display="block" color="text.secondary">{credential.issuer}</Typography>
        </TableCell>
        <TableCell>{credential.expiresOn ? toJalali(credential.expiresOn) : t('iam:credential.no_expiry')}</TableCell>
        <TableCell>
          <span data-testid="credential-validity" data-state={credential.validity}>
            <StatusChip tone={validityTone(credential.validity)} labelKey={`iam:credential.validity.${credential.validity}`} />
          </span>
          {typeof days === 'number' && credential.validity === 'Expiring' ? (
            <Typography variant="caption" display="block">{t('iam:credential.days_left', { days: faDigits(days) })}</Typography>
          ) : null}
          <Typography variant="caption" display="block" color="text.secondary">
            {t(`iam:credential.verification.${credential.verification}`)}
          </Typography>
          {/* AP-INT-04 (AC-IAM-023.1): علت ماشین‌خوان نتیجه استعلام؛ علت ناشناخته نمایش داده نمی‌شود. */}
          {credential.verificationReason && t(`iam:credential.verification_reasons.${credential.verificationReason}`, { defaultValue: '' }) ? (
            <Typography variant="caption" display="block" color="text.secondary" data-testid="credential-verification-reason">
              {t(`iam:credential.verification_reasons.${credential.verificationReason}`)}
            </Typography>
          ) : null}
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
            {credential.attachment ? (
              <Button size="small" onClick={() => void download()} data-testid="credential-download">
                {t('iam:credential.download')}
              </Button>
            ) : null}
            {canWrite ? (
              <>
                <Button size="small" component="label" disabled={attach.isPending}>
                  {t(credential.attachment ? 'iam:credential.replace_file' : 'iam:credential.attach')}
                  <input
                    hidden
                    type="file"
                    accept="application/pdf,image/png,image/jpeg"
                    data-testid="credential-file"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) {
                        attach.mutate(file);
                      }
                      event.target.value = '';
                    }}
                  />
                </Button>
                <Button size="small" onClick={onEdit} data-testid="credential-edit">
                  {t('common:actions.edit')}
                </Button>
                <Button size="small" onClick={() => verify.mutate()} disabled={verify.isPending} data-testid="credential-verify">
                  {t('iam:credential.verify')}
                </Button>
                <Button size="small" color="error" onClick={() => setConfirm(true)} data-testid="credential-delete">
                  {t('common:actions.delete')}
                </Button>
              </>
            ) : null}
          </Stack>
          {actionError ? <ErrorState error={actionError} compact /> : null}
        </TableCell>
      </TableRow>
      <ConfirmDialog
        open={confirm}
        titleKey="iam:credential.delete_title"
        messageKey="iam:credential.delete_message"
        confirmKey="common:actions.delete"
        destructive
        busy={remove.isPending}
        error={remove.error}
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirm(false)}
      />
    </>
  );
}

export function ProviderCredentialsSection({ providerId, canWrite }: { providerId: string; canWrite: boolean }) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const [editing, setEditing] = useState<ProviderCredentialView | null | undefined>(undefined);
  const list = useQuery({
    queryKey: credentialKeys.list(session?.tenantId, providerId),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/identity/providers/{providerId}/credentials', { params: { path: { providerId } } })),
  });

  return (
    <Stack spacing={1.5} data-testid="provider-credentials">
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h3">{t('iam:credential.title')}</Typography>
        {canWrite ? (
          <Button variant="outlined" onClick={() => setEditing(null)} data-testid="credential-add">
            {t('iam:credential.add')}
          </Button>
        ) : null}
      </Stack>
      {list.isPending ? <LoadingState rows={2} /> : null}
      {list.error ? <ErrorState error={list.error} onRetry={() => void list.refetch()} /> : null}
      {list.data ? (
        <>
          {list.data.missingRequired.length > 0 ? (
            <Alert severity="warning" data-testid="credential-missing">
              {t('iam:credential.missing_required', {
                types: list.data.missingRequired.map((type) => t(`iam:credential.types.${type}`)).join(t('iam:credential.separator')),
              })}
            </Alert>
          ) : null}
          {list.data.items.some((item) => item.validity === 'Expired') ? (
            <Alert severity="error" data-testid="credential-expired-alert">
              {t('iam:credential.expired_hint')}
            </Alert>
          ) : null}
          {list.data.items.length === 0 ? (
            <Typography color="text.secondary">{t('iam:credential.empty')}</Typography>
          ) : (
            <Table size="small" aria-label={t('iam:credential.title')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('iam:credential.type')}</TableCell>
                  <TableCell>{t('iam:credential.number')}</TableCell>
                  <TableCell>{t('iam:credential.expires_on')}</TableCell>
                  <TableCell>{t('iam:credential.status')}</TableCell>
                  <TableCell>{t('iam:credential.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.data.items.map((credential) => (
                  <CredentialRow
                    key={credential.id}
                    providerId={providerId}
                    credential={credential}
                    canWrite={canWrite}
                    onEdit={() => setEditing(credential)}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </>
      ) : null}
      {editing !== undefined ? (
        <CredentialDialog providerId={providerId} credential={editing} onClose={() => setEditing(undefined)} />
      ) : null}
    </Stack>
  );
}
